import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { FileManagerService, FileItem } from './fileManager.service';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.services';

@Component({
  selector: 'app-file-manager',
  templateUrl: './fileManager.component.html',
  styleUrl: './fileManager.component.scss',
  standalone: false,
})
export class FileManagerComponent implements OnInit, OnDestroy {
  items: FileItem[] = [];
  currentPath: string = '/';
  breadcrumbs: Array<{ name: string; path: string }> = [{ name: 'Root', path: '/' }];
  isLoading = false;
  error: string | null = null;
  viewMode: 'grid' | 'list' = 'grid';
  selectedItems: Set<string> = new Set();
  searchQuery: string = '';
  filteredItems: FileItem[] = [];
  sortBy: 'name' | 'date' | 'size' | 'type' = 'name';
  sortOrder: 'asc' | 'desc' = 'asc';
  previewItem: FileItem | null = null;
  showPreview = false;
  userId: string = '';

  private subscriptions: Subscription[] = [];
  private listSubscription: Subscription | null = null;
  private destroy$ = new Subject<void>();

  /** Normalize path for consistent listing (backend expects '/' or path without trailing slash) */
  private normalizePath(path: string): string {
    if (!path || path.trim() === '') return '/';
    const p = path.replace(/\/+/g, '/').replace(/\/$/, '').trim();
    return p === '' ? '/' : (p.startsWith('/') ? p : '/' + p);
  }

  constructor(
    private fileManagerService: FileManagerService,
    public auth: AuthService
  ) {}

  ngOnInit(): void {
    // Get user from Auth0
    this.auth.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: any) => {
        const user = profile?.profile ?? profile;
        if (user?.email || user?.sub) {
          this.userId = user._id || user.email || '';
          if (this.userId) {
            this.loadItems('/');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.listSubscription?.unsubscribe();
    this.listSubscription = null;
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadItems(path: string): void {
    if (!this.userId) {
      this.error = 'User not authenticated';
      return;
    }

    const normalizedPath = this.normalizePath(path);
    this.isLoading = true;
    this.error = null;
    this.currentPath = normalizedPath;
    this.updateBreadcrumbs(normalizedPath);

    if (this.listSubscription) {
      this.listSubscription.unsubscribe();
      this.listSubscription = null;
    }

    this.listSubscription = this.fileManagerService.listFiles(this.userId, normalizedPath).subscribe({
      next: (files) => {
        this.items = files ?? [];
        this.applyFilters();
        this.isLoading = false;
        this.listSubscription = null;
      },
      error: (err) => {
        this.error = 'Failed to load files';
        this.isLoading = false;
        this.listSubscription = null;
        console.error('Error loading files:', err);
      }
    });
  }


  navigateTo(path: string): void {
    const normalizedPath = this.normalizePath(path);
    if (normalizedPath === this.currentPath) return;
    this.selectedItems.clear();
    this.loadItems(normalizedPath);
  }

  navigateToFolder(folder: FileItem): void {
    const path = folder?.path ? this.normalizePath(folder.path) : '/';
    this.navigateTo(path);
  }

  updateBreadcrumbs(path: string): void {
    if (path === '/') {
      this.breadcrumbs = [{ name: 'Root', path: '/' }];
    } else {
      const pathParts = path.split('/').filter(p => p);
      this.breadcrumbs = [{ name: 'Root', path: '/' }];

      let currentPath = '';
      pathParts.forEach(part => {
        currentPath += '/' + part;
        this.breadcrumbs.push({ name: part, path: currentPath });
      });
    }
  }

  selectItem(item: FileItem, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      // Multi-select with Ctrl/Cmd
      if (this.selectedItems.has(item.id)) {
        this.selectedItems.delete(item.id);
      } else {
        this.selectedItems.add(item.id);
      }
    } else {
      // Single select
      if (item.type === 'folder') {
        this.navigateToFolder(item);
      } else {
        this.selectedItems.clear();
        this.selectedItems.add(item.id);
        this.previewFile(item);
      }
    }
  }

  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  onSearch(query: string): void {
    this.searchQuery = query;
    if (!query.trim()) {
      this.applyFilters();
      return;
    }

    // Use search API if query is provided
    if (!this.userId) return;

    this.isLoading = true;
    const sub = this.fileManagerService.searchFiles(this.userId, query).subscribe({
      next: (files) => {
        this.items = files;
        this.filteredItems = files;
        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Search failed';
        this.isLoading = false;
        console.error('Error searching files:', err);
      }
    });

    this.subscriptions.push(sub);
  }

  applyFilters(): void {
    let filtered = [...this.items];

    // Apply search filter
    if (this.searchQuery.trim()) {
      const searchLower = this.searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchLower) ||
        item.path.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (this.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          const dateA = a.modified ? new Date(a.modified).getTime() : 0;
          const dateB = b.modified ? new Date(b.modified).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'size':
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          comparison = sizeA - sizeB;
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          if (comparison === 0) {
            comparison = a.name.localeCompare(b.name);
          }
          break;
      }

      return this.sortOrder === 'asc' ? comparison : -comparison;
    });

    this.filteredItems = filtered;
  }

  setSortBy(sortBy: 'name' | 'date' | 'size' | 'type'): void {
    if (this.sortBy === sortBy) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = sortBy;
      this.sortOrder = 'asc';
    }
    this.applyFilters();
  }

  refresh(): void {
    this.loadItems(this.currentPath);
  }

  previewFile(file: FileItem): void {
    if (!this.userId || file.type === 'folder') return;

    this.previewItem = {
      id: file.id || '',
      name: file.name || '',
      path: file.path || '',
      type: file.type || 'file',
      mimeType: file.mimeType,
      size: file.size,
      modified: file.modified,
      thumbnail: file.thumbnail,
      icon: file.icon,
      children: file.children
    };
    this.showPreview = true;

    // If file has thumbnail, use it; otherwise try to get it from download
    if (!file.thumbnail && file.type === 'file' && this.previewItem) {
      const sub = this.fileManagerService.getFileDetails(this.userId, file.id).subscribe({
        next: (fileDetails) => {
          if (fileDetails && fileDetails.thumbnail && this.previewItem) {
            this.previewItem.thumbnail = fileDetails.thumbnail;
          }
        },
        error: (err) => {
          console.error('Error loading file details:', err);
        }
      });
      this.subscriptions.push(sub);
    }
  }

  closePreview(): void {
    this.showPreview = false;
    this.previewItem = null;
  }

  getFileIcon(item: FileItem): string {
    if (item.icon) return item.icon;
    if (item.type === 'folder') return 'fa-folder';

    if (!item.mimeType) return 'fa-file';

    const mimeType = item.mimeType.toLowerCase();

    // Images
    if (mimeType.startsWith('image/')) return 'fa-file-image';
    // Videos
    if (mimeType.startsWith('video/')) return 'fa-file-video';
    // Audio
    if (mimeType.startsWith('audio/')) return 'fa-file-audio';
    // PDF
    if (mimeType === 'application/pdf') return 'fa-file-pdf';
    // Word documents
    if (mimeType.includes('word') || mimeType.includes('document')) return 'fa-file-word';
    // Excel/Spreadsheets
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('sheet')) return 'fa-file-excel';
    // PowerPoint/Presentations
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'fa-file-powerpoint';
    // Archives
    if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('rar') || mimeType.includes('7z')) return 'fa-file-archive';
    // Code files
    if (mimeType.includes('javascript') || mimeType.includes('json') || mimeType.includes('code') ||
        mimeType.includes('typescript') || mimeType.includes('python') || mimeType.includes('java') ||
        mimeType.includes('html') || mimeType.includes('css') || mimeType.includes('xml')) return 'fa-file-code';
    // Text files
    if (mimeType.includes('text') || mimeType.includes('plain')) return 'fa-file-alt';
    // CSV
    if (mimeType.includes('csv')) return 'fa-file-csv';

    return 'fa-file';
  }

  getFileIconColor(item: FileItem): string {
    if (item.type === 'folder') return '#667eea';

    if (!item.mimeType) return '#8a8d91';

    const mimeType = item.mimeType.toLowerCase();

    if (mimeType.startsWith('image/')) return '#e74c3c';
    if (mimeType.startsWith('video/')) return '#9b59b6';
    if (mimeType.startsWith('audio/')) return '#3498db';
    if (mimeType === 'application/pdf') return '#e74c3c';
    if (mimeType.includes('word') || mimeType.includes('document')) return '#2c3e50';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType.includes('sheet')) return '#27ae60';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '#e67e22';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return '#f39c12';
    if (mimeType.includes('javascript') || mimeType.includes('json') || mimeType.includes('code') ||
        mimeType.includes('typescript') || mimeType.includes('python') || mimeType.includes('java') ||
        mimeType.includes('html') || mimeType.includes('css') || mimeType.includes('xml')) return '#3498db';
    if (mimeType.includes('text') || mimeType.includes('plain')) return '#95a5a6';
    if (mimeType.includes('csv')) return '#27ae60';

    return '#8a8d91';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '—';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '—';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return 'Yesterday ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    } catch {
      return dateString;
    }
  }

  goBack(): void {
    if (this.breadcrumbs.length > 1) {
      const parentPath = this.breadcrumbs[this.breadcrumbs.length - 2].path;
      this.navigateTo(parentPath);
    }
  }

  getDisplayItems(): FileItem[] {
    return this.filteredItems.length > 0 ? this.filteredItems : this.items;
  }

  /**
   * Handle file upload
   */
  onFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0 || !this.userId) return;

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]; // Remove data URL prefix
      const mimeType = file.type;

      // Generate thumbnail for images
      let thumbnail: string | undefined;
      if (mimeType.startsWith('image/')) {
        thumbnail = reader.result as string;
      }

      this.isLoading = true;
      const sub = this.fileManagerService.uploadFile(
        this.userId,
        file.name,
        base64,
        mimeType,
        this.currentPath,
        thumbnail
      ).subscribe({
        next: (result) => {
          if (result.code === 201) {
            this.loadItems(this.currentPath); // Reload files
          } else {
            this.error = result.message || 'Upload failed';
            this.isLoading = false;
          }
        },
        error: (err) => {
          this.error = 'Upload failed';
          this.isLoading = false;
          console.error('Error uploading file:', err);
        }
      });

      this.subscriptions.push(sub);
    };

    reader.readAsDataURL(file);
    input.value = ''; // Reset input
  }

  /**
   * Create a new folder
   */
  async createFolder(): Promise<void> {
    if (!this.userId) return;

    const { value: folderName } = await Swal.fire({
      title: 'Create New Folder',
      input: 'text',
      inputLabel: 'Folder name',
      inputPlaceholder: 'Enter folder name...',
      showCancelButton: true,
      confirmButtonText: 'Create',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return 'Please enter a folder name';
        }
        // Check for invalid characters
        if (/[<>:"/\\|?*]/.test(value)) {
          return 'Folder name contains invalid characters';
        }
        return null;
      },
      background: '#1a1a2e',
      color: '#fff',
      inputAttributes: {
        autocapitalize: 'off',
        autocorrect: 'off'
      }
    });

    if (!folderName || !folderName.trim()) return;

    this.isLoading = true;
    const sub = this.fileManagerService.createFolder(this.userId, folderName.trim(), this.currentPath).subscribe({
      next: (result) => {
        if (result.code === 201) {
          this.loadItems(this.currentPath); // Reload files
          Swal.fire({
            title: 'Folder Created',
            text: `Folder "${folderName.trim()}" has been created successfully.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
            background: '#1a1a2e',
            color: '#fff',
          });
        } else {
          this.error = result.message || 'Failed to create folder';
          this.isLoading = false;
          Swal.fire({
            title: 'Error',
            text: result.message || 'Failed to create folder',
            icon: 'error',
            background: '#1a1a2e',
            color: '#fff',
          });
        }
      },
      error: (err) => {
        this.error = 'Failed to create folder';
        this.isLoading = false;
        console.error('Error creating folder:', err);
        Swal.fire({
          title: 'Error',
          text: 'Failed to create folder. Please try again.',
          icon: 'error',
          background: '#1a1a2e',
          color: '#fff',
        });
      }
    });

    this.subscriptions.push(sub);
  }

  /**
   * Delete selected files/folders
   */
  deleteSelected(): void {
    if (!this.userId || this.selectedItems.size === 0) return;

    if (!confirm(`Are you sure you want to delete ${this.selectedItems.size} item(s)?`)) return;

    const deletePromises = Array.from(this.selectedItems).map(id => {
      return this.fileManagerService.deleteFile(this.userId, id).toPromise();
    });

    Promise.all(deletePromises).then(() => {
      this.selectedItems.clear();
      this.loadItems(this.currentPath); // Reload files
    }).catch(err => {
      this.error = 'Failed to delete some items';
      console.error('Error deleting files:', err);
    });
  }
}
